import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
  ValidatorFn,
  Validators as V
} from '@angular/forms';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { Moment } from 'moment';
import { Decimal } from 'decimal.js-light';

import { FormErrors } from '@personal-area/global/form-validation/models';

// the need in this validators is the non-trimming angular standard behavior
// see https://github.com/angular/angular/issues/8503

enum PhoneValidationErrorEnum {
  InvalidCountry = 'INVALID_COUNTRY',
  TooLong = 'TOO_LONG',
  TooShort = 'TOO_SHORT',
  NotANumber = 'NOT_A_NUMBER'
}

function isEmptyInputValue(value: any): boolean {
  // we don't check for string here so it also works with arrays
  return value == null || value.length === 0;
}

export class Validators {
  public static required(control: AbstractControl): ValidationErrors | null {
    if (!control.value || (typeof control.value === 'string' && !control.value.trim())) {
      return {
        required: true
      };
    }

    return null;
  }

  public static requiredTrue(control: AbstractControl): ValidationErrors | null {
    return V.requiredTrue(control);
  }

  public static email(control: AbstractControl): ValidationErrors | null {
    if (control.value && typeof control.value === 'string' && !control.value.trim().match(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/)) {
      return {
        email: true
      };
    }
    return null;
  }

  public static min(min: number | Decimal): ValidatorFn {
    return (control)=>{
      if (isEmptyInputValue(control.value) || isEmptyInputValue(min)) {
        return null;
        // don't validate empty values to allow optional controls
      }
      let value: number | Decimal;

      if (typeof min === 'number') {
        value = parseFloat(control.value);
      } else {
        try {
          value = new Decimal(control.value);
        } catch (_) {
          value = NaN;
        }
      }

      // Controls with NaN values after parsing should be treated as not having a
      // minimum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
      const invalidNumber = typeof value === 'number' && !isNaN(value) && value < Number(min);
      const invalidDecimal = typeof value === 'object'
        && Object.getPrototypeOf(value) === Decimal.prototype
        && value.lessThan(min);

      return invalidNumber || invalidDecimal ? {
        'min': {
          'min': min,
          'actual': control.value
        }
      } : null;
    };
  }

  public static max(max: number | Decimal): ValidatorFn {
    return (control)=>{
      if (isEmptyInputValue(control.value) || isEmptyInputValue(max)) {
        return null;
        // don't validate empty values to allow optional controls
      }
      let value;

      if (typeof max === 'number') {
        value = parseFloat(control.value);
      } else {
        try {
          value = new Decimal(control.value);
        } catch (_) {
          value = NaN;
        }
      }

      // Controls with NaN values after parsing should be treated as not having a
      // maximum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-max
      const invalidNumber = typeof value === 'number' && !isNaN(value) && value > Number(max);
      const invalidDecimal = typeof value === 'object'
        && Object.getPrototypeOf(value) === Decimal.prototype
        && value.greaterThan(max);

      return invalidNumber || invalidDecimal ? {
        'max': {
          'max': max,
          'actual': control.value
        }
      } : null;
    };
  }

  public static minLength(length: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value && (typeof control.value === 'string' && control.value.trim().length < length)) {
        return {
          minlength: true
        };
      }

      return null;
    };
  }

  public static maxLength(length: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value && typeof control.value === 'string' && control.value.trim().length > length) {
        return {
          maxlength: true
        };
      }

      return null;
    };
  }

  public static pattern(pattern: RegExp | string): ValidatorFn {
    return V.pattern(pattern);
  }

  /**
   * Проверка допустимых для ввода символов
   * @param control
   */
  public static availableChars(control: AbstractControl): ValidationErrors | null {
    if (typeof control.value === 'string' && !control.value.match(/^[\x21-\x7F ]*$/g)) {
      return {
        [FormErrors.availableChars]: true
      };
    }
    return null;
  }

  public static passwordChars(control: AbstractControl): ValidationErrors | null {
    const check = (value: string) => {
      if (!value.match(/^[\x21-\x7F ]*$/)) {
        return false;
      }
      let l, u, n: boolean;
      value.split('').forEach((char) => {
        if (char.match(/[0-9]/)) {
          n = true;
        } else if (char.toLowerCase() === char) {
          l = true;
        } else if (char.toUpperCase() === char) {
          u = true;
        }
      });
      return l && u && n;
    };
    if (typeof control.value === 'string' && !check(control.value)) {
      return {
        passwordChars: true
      };
    }
    return null;
  }

  public static minAmount(amount: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value && control.value.length < amount) {
        return {
          minamount: true
        };
      }

      return null;
    };
  }

  public static maxAmount(amount: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value && control.value.length > amount) {
        return {
          maxamount: true
        };
      }

      return null;
    };
  }

  public static compose(validators: ValidatorFn[]): ValidatorFn {
    return V.compose(validators);
  }

  public static composeAsync(validators: AsyncValidatorFn[]): AsyncValidatorFn {
    return V.composeAsync(validators);
  }

  public static numericFieldMinLengthValidator(minLength: number): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      return control.value.toString().length < minLength ? { 'minLength': { value: control.value, expected: minLength } } : null;
    };
  }

  public static numericFieldMaxLengthValidator(maxLength: number): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      return control.value.toString().length > maxLength ? { 'maxLength': { value: control.value, expected: maxLength } } : null;
    };
  }

  public static uppercaseLetter(control: AbstractControl): ValidationErrors | null {
    if (control.value && typeof control.value === 'string' && !control.value.match(/[A-Z]/g)) {
      return {
        uppercaseLetter: true
      };
    }
    return null;
  }

  public static lowercaseLetter(control: AbstractControl): ValidationErrors | null {
    if (control.value && typeof control.value === 'string' && !control.value.match(/[a-z]/g)) {
      return {
        lowercaseLetter: true
      };
    }

    return null;
  }

  public static number(control: AbstractControl): ValidationErrors | null {
    if (control.value && typeof control.value === 'string' && !control.value.match(/[0-9]/g)) {
      return {
        number: true
      };
    }
    return null;
  }

  public static latinLetters(control: AbstractControl): ValidationErrors {
    if (control.value && typeof control.value === 'string' && !control.value.match(/.*[a-zA-Z].*/g)) {
      return {
        latinLetters: true
      };
    }

    return null;
  }

  /**
   * Проверка валидности номера телефона
   * с помощью библиотеки libphonenumber
   */
  public static phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    const val = control.value;

    if (!val) {
      return null;
    }

    const phone = val.includes('+') ? val : `+${val}`;
    try {
      parsePhoneNumberWithError(phone);
      return null;
    } catch (error) {
      switch (error.message) {
        case PhoneValidationErrorEnum.InvalidCountry:
          return {
            invalidCountryPhone: true
          };
        case PhoneValidationErrorEnum.TooLong:
          return {
            longPhone: true
          };
        case PhoneValidationErrorEnum.TooShort:
        case PhoneValidationErrorEnum.NotANumber:
          return {
            phone: true
          };
        default:
          return null;
      }
    }
  }

  public static age(minAge: number): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const val = control.value;
      if (!val) {
        return null;
      }
      const now = new Date();
      return +new Date(now.setFullYear(now.getFullYear() - minAge)) - (val as Moment).valueOf() < 0
        ? { age: true }
        : null;
    };
  }

  public static forbidden(forbiddenVal: string): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const val = control.value;
      if (!val) {
        return null;
      }
      return val === forbiddenVal ? { forbidden: true } : null;
    };
  }
}
